import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useTranslation } from "react-i18next";

import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";

import { Link } from "react-router-dom";

import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { Button } from "@/components/Button/Button";
import { InputField } from "@/components/InputField/InputField";

import {
	FundingCategories,
	FundingCountries,
	FundingMaType,
	FundingRoundsValues,
	MaStatusValues,
} from "@/enum/funding-ma.enum";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	MultiAutoComplete,
	MultiAutoCompleteItem,
} from "@/components/MultiAutoComplete/MultiAutoComplete";

import { Radio } from "@/components/Radio/Radio";

import { Information } from "@/components/Information/Information";

import {
	useCreateFundingMaMutation,
	useUpdateFundingMaMutation,
	usePreviewFundingMaMutation,
} from "@/pages/Private/redux/fundingMa/fundingMa.api";

import { Table, TableColumn } from "@/components/Table/Table";
import { Icon } from "@/components/Icon/Icon";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { Modal } from "@/components";

import { WebsiteIcon } from "@/components/WebsiteIcon/WebsiteIcon";

import { resolveSourceMode } from "../../List/pages/ListCreate/utils/sourceMode";

import { FundingSignal } from "../schema/leadsOnTrees";
import { CreateFundingMa, CreateFundingMaSchema, FundingMa } from "../schema/funding-ma";
import { List } from "../../List/schema/list";

interface CreateFundingMaFormProps {
	fundingMa?: FundingMa | null;
	list: List;
	handleSelect?: (fundingMa: FundingMa | null) => void;
	handleDuplicate?: (fundingMa: FundingMa | null) => void;
}

export enum LastIxDaysOrTimeframe {
	LAST_X_DAYS = "last_x_days",
	TIMEFRAME = "timeframe",
}

export const CreateFundingMaForm: FunctionComponent<CreateFundingMaFormProps> = ({
	fundingMa,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`fundingMa.${key}`);

	const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
	const [createFundingMa, { error, isLoading }] = useCreateFundingMaMutation();
	const [updateFundingMa, { error: updateError, isLoading: updateIsLoading }] =
		useUpdateFundingMaMutation();
	const [
		previewFundingMa,
		{ error: previewError, isLoading: previewIsLoading, data: previewFundingMaData },
	] = usePreviewFundingMaMutation();

	const [lastIxDaysOrTimeframe, setLastIxDaysOrTimeframe] = useState<LastIxDaysOrTimeframe>(
		LastIxDaysOrTimeframe.LAST_X_DAYS
	);

	const handlePreviewJob = async () => {
		const result = await previewFundingMa({
			listId: list.id,
			countries: formValues.countries,
			round: formValues.round,
			category: formValues.category,
			lastXDays: formValues.lastXDays,
			dateFrom: formValues.dateFrom,
			dateTo: formValues.dateTo,
			type: formValues.type,
			name: formValues.name || "Preview",
		}).unwrap();

		if (result.data) {
			setShowPreviewModal(true);
		}
	};

	const previewColumns: TableColumn[] = useMemo(
		() => [
			{
				title: "CompanyName",
				uid: "company_name",
				render: (row: FundingSignal) => (
					<div className="flex items-center justify-start gap-1 flex-row">
						{row?.companyLogo ? (
							<img
								alt={row?.companyName}
								className="w-4 h-4 rounded-full"
								referrerPolicy="no-referrer"
								src={row?.companyLogo}
							/>
						) : (
							""
						)}
						<div className="text-sm font-medium max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis">
							{row?.companyName}
						</div>
						{row?.companyLinkedin && (
							<Link target="_blank" to={row.companyLinkedin}>
								<LinkedinIcon status="checked" />
							</Link>
						)}
						{row?.companyWebsite && (
							<Link target="_blank" to={row.companyWebsite}>
								<WebsiteIcon status="checked" />
							</Link>
						)}
					</div>
				),
			},
			{
				title: "Funding Amount",
				uid: "funding_amount",
				minWidth: 120,
				render: (row: FundingSignal) => (
					<div className="flex items-end justify-end w-full text-right">
						{row?.amount?.toLocaleString()} {row?.currency}
					</div>
				),
			},
			{
				title: "Round Type",
				uid: "round_type",
				render: (row: FundingSignal) => (
					<div className="flex items-start justify-start gap-2">
						<div className="text-sm font-medium max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis">
							{row?.roundType}
						</div>
						{row.sources?.[0]?.url && (
							<Link target="_blank" to={row.sources[0].url}>
								<Icon icon={faExternalLink} />
							</Link>
						)}
					</div>
				),
			},
			{
				title: "Company HQ",
				uid: "company_hq",
				render: (row: FundingSignal) => (
					<div className="flex items-start justify-start">{row?.companyCountry}</div>
				),
			},
			{
				title: "Date seen",
				uid: "date_seen",
				render: (row: FundingSignal) => (
					<div className="flex items-start justify-start">
						{row?.announcedDate
							? format(new Date(row.announcedDate), "dd.MM.yyyy")
							: row?.discoveredAt
								? format(new Date(row.discoveredAt), "dd.MM.yyyy")
								: "-"}
					</div>
				),
			},
		],
		[ts]
	);

	const previewMaColumns: TableColumn[] = useMemo(
		() => [
			{
				title: "CompanyName",
				uid: "company_name",
				render: (row: FundingSignal) => (
					<div className="flex items-center justify-start gap-1 flex-row">
						{row?.companyLogo ? (
							<img
								alt={row?.companyName}
								className="w-4 h-4 rounded-full"
								referrerPolicy="no-referrer"
								src={row?.companyLogo}
							/>
						) : (
							""
						)}
						<div className="text-sm font-medium max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis">
							{row?.companyName}
						</div>
						{row?.companyLinkedin && (
							<Link target="_blank" to={row.companyLinkedin}>
								<LinkedinIcon status="checked" />
							</Link>
						)}
						{row?.companyWebsite && (
							<Link target="_blank" to={row.companyWebsite}>
								<WebsiteIcon status="checked" />
							</Link>
						)}
					</div>
				),
			},
			{
				title: "Amount",
				uid: "amount",
				minWidth: 120,
				render: (row: FundingSignal) => (
					<div className="flex items-end justify-end w-full text-right">
						{row?.amount?.toLocaleString()} {row?.currency}
					</div>
				),
			},
			{
				title: "Signal Type",
				uid: "signal_type",
				render: (row: FundingSignal) => (
					<div className="flex items-start justify-start gap-2">
						<div className="text-sm font-medium max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis">
							{row?.signalType.capitalizeString()}
						</div>
						{row.sources?.[0]?.url && (
							<Link target="_blank" to={row.sources[0].url}>
								<Icon icon={faExternalLink} />
							</Link>
						)}
					</div>
				),
			},
			{
				title: "Acquiring Company",
				uid: "acquiring_company",
				render: (row: FundingSignal) => (
					<div className="flex items-center justify-start gap-1 flex-row">
						{row?.acquiringCompany?.logo ? (
							<img
								alt={row?.acquiringCompany?.name}
								className="w-4 h-4 rounded-full"
								referrerPolicy="no-referrer"
								src={row?.acquiringCompany.logo}
							/>
						) : (
							""
						)}
						<div className="text-sm font-medium max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis">
							{row?.acquiringCompany?.name}
						</div>
						{row?.acquiringCompany?.linkedin && (
							<Link target="_blank" to={row.acquiringCompany.linkedin}>
								<LinkedinIcon status="checked" />
							</Link>
						)}
						{row?.acquiringCompany?.website && (
							<Link target="_blank" to={row.acquiringCompany.website}>
								<WebsiteIcon status="checked" />
							</Link>
						)}
					</div>
				),
			},
			{
				title: "Company HQ",
				uid: "company_hq",
				render: (row: FundingSignal) => (
					<div className="flex items-start justify-start">{row?.companyCountry}</div>
				),
			},
			{
				title: "Date seen",
				uid: "date_seen",
				render: (row: FundingSignal) => (
					<div className="flex items-start justify-start">
						{row?.announcedDate
							? format(new Date(row.announcedDate), "dd.MM.yyyy")
							: row?.discoveredAt
								? format(new Date(row.discoveredAt), "dd.MM.yyyy")
								: "-"}
					</div>
				),
			},
		],
		[ts]
	);

	const initialState = {
		name: "",
		countries: [],
		round: [],
		category: [],
		type: resolveSourceMode(Object.values(FundingMaType), FundingMaType.FUNDING),
		lastXDays: null,
		dateFrom: null,
		dateTo: null,
		listId: list.id,
	};

	const {
		reset,
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CreateFundingMa>({
		defaultValues: initialState,
		resolver: zodResolver(CreateFundingMaSchema),
	});

	const formValues = watch();

	useEffect(() => {
		if (fundingMa) {
			const formState = {
				name: fundingMa.name,
				countries: fundingMa.countries,
				round: fundingMa.round,
				category: fundingMa.category,
				type: fundingMa.type,
				lastXDays: fundingMa.lastXDays,
				dateFrom: fundingMa.dateFrom,
				dateTo: fundingMa.dateTo,
				listId: list.id,
			};

			if (handleDuplicate) {
				reset({
					...formState,
				});
			} else {
				reset({
					id: fundingMa.id,
					...formState,
				});
			}
		} else {
			reset();
		}
	}, [fundingMa]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateFundingMa) => {
		try {
			if (values.id) {
				await updateFundingMa({ id: values.id, ...values }).unwrap();
			} else {
				await createFundingMa({ ...values, listId: list.id }).unwrap();
			}

			handleSelect?.(null);
			handleDuplicate?.(null);

			reset();
		} catch (err) {
			console.error(err);
		}
	};
	const buttonTitle =
		handleDuplicate && fundingMa?.id
			? t("basics.copyCriteria")
			: fundingMa?.id
				? t("basics.save")
				: t("basics.addCriteria");

	const industries = useMemo(() => {
		return [...FundingCategories].sort((a, b) => a.title.localeCompare(b.title));
	}, []);

	return (
		<>
			<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
				<div className="text-md mb-5">Add a source</div>
				<div className="flex flex-row gap-4 w-full mb-4 flex-wrap">
					<div className="w-auto">
						<Button
							color={
								formValues.type === FundingMaType.FUNDING
									? ButtonColor.ACTION
									: ButtonColor.ACTION_SECONDARY
							}
							size={ButtonSize.ML}
							title="Startup Funding"
							onClick={() => setValue("type", FundingMaType.FUNDING)}
						/>
					</div>
					<div className="w-auto">
						<Button
							color={
								formValues.type === FundingMaType.MA
									? ButtonColor.ACTION
									: ButtonColor.ACTION_SECONDARY
							}
							size={ButtonSize.ML}
							title="M&A Activities"
							onClick={() => setValue("type", FundingMaType.MA)}
						/>
					</div>
				</div>
				<div className="flex flex-row gap-4 w-full mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setValue("name", event.target.value, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("nameLabel")}
						name={"name"}
						placeholder={ts("namePlaceholder")}
						value={formValues.name ?? ""}
					/>
				</div>
				{formValues.type === FundingMaType.FUNDING ? (
					<div>
						<div className="flex flex-row gap-4 w-full mb-4">
							<MultiAutoComplete
								data={FundingRoundsValues as unknown as MultiAutoCompleteItem[]}
								handleSelect={function (value?: MultiAutoCompleteItem[]): void {
									if (value?.length && value?.length > 0) {
										setValue("round", value?.map((r) => r.id.toString()) || [], {
											shouldDirty: true,
											shouldValidate: true,
										});
									} else {
										setValue("round", null, {
											shouldDirty: true,
											shouldValidate: true,
										});
									}
								}}
								information={ts("roundInformation")}
								label={ts("roundLabel")}
								placeholder={ts("roundPlaceholder")}
								selectedItems={
									formValues?.round?.map((r) => ({
										id: r,
										title: FundingRoundsValues.find((rr) => rr.id === r)?.title || "",
									})) || []
								}
							/>
						</div>
						<div className="flex flex-row gap-4 w-full mb-4">
							<MultiAutoComplete
								data={
									FundingCountries.sort((a, b) =>
										a.title.localeCompare(b.title)
									) as unknown as MultiAutoCompleteItem[]
								}
								handleSelect={function (value?: MultiAutoCompleteItem[]): void {
									if (value?.length && value?.length > 0) {
										setValue("countries", value.map((r) => r.id.toString()) || [], {
											shouldDirty: true,
											shouldValidate: true,
										});
									} else {
										setValue("countries", null, {
											shouldDirty: true,
											shouldValidate: true,
										});
									}
								}}
								information={ts("countriesInformation")}
								label={ts("countriesLabel")}
								placeholder={ts("countriesPlaceholder")}
								selectedItems={
									formValues.countries?.map(
										(r) => FundingCountries.find((rr) => rr.id === r) ?? { id: r, title: r }
									) || []
								}
							/>
						</div>
						<div className="flex flex-row gap-4 w-full mb-4">
							<MultiAutoComplete
								data={industries as unknown as MultiAutoCompleteItem[]}
								handleSelect={function (value?: MultiAutoCompleteItem[]): void {
									if (value?.length && value?.length > 0) {
										setValue("category", value.map((r) => r.id.toString()) || [], {
											shouldDirty: true,
											shouldValidate: true,
										});
									} else {
										setValue("category", null, {
											shouldDirty: true,
											shouldValidate: true,
										});
									}
								}}
								information={ts("categoryInformation")}
								label={ts("categoryLabel")}
								placeholder={ts("categoryPlaceholder")}
								selectedItems={
									formValues.category?.map(
										(r) => industries.find((rr) => rr.id === r) ?? { id: r, title: r }
									) || []
								}
							/>
						</div>
						<div className="flex flex-col gap-4 w-full mt-8">
							<label className="flex flex-row items-center block text-sm font-medium text-gray-900 appearance-none">
								Timeframe for the search
								<span className="ml-1">
									<Information
										information={"Select the timeframe for the search (last X days or range)."}
									/>
								</span>
							</label>
							<div className="flex flex-row gap-4 w-full">
								<Radio
									isChecked={lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.LAST_X_DAYS}
									onChange={() => setLastIxDaysOrTimeframe(LastIxDaysOrTimeframe.LAST_X_DAYS)}
								>
									Last X Days
								</Radio>
								<Radio
									isChecked={lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.TIMEFRAME}
									onChange={() => setLastIxDaysOrTimeframe(LastIxDaysOrTimeframe.TIMEFRAME)}
								>
									Range
								</Radio>
							</div>
							{lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.LAST_X_DAYS && (
								<div className="flex flex-row gap-4 w-full mb-4">
									<InputField
										error={!!errors.lastXDays?.message}
										handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
											if (!isNaN(+event.target.value) && +event.target.value > 0) {
												setValue("lastXDays", Number(event.target.value), {
													shouldDirty: true,
													shouldValidate: true,
												});

												setValue("dateFrom", null, {
													shouldDirty: true,
													shouldValidate: true,
												});

												setValue("dateTo", null, {
													shouldDirty: true,
													shouldValidate: true,
												});
											} else if (event.target.value === "") {
												setValue("lastXDays", null, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}
										}}
										label={ts("lastXDaysLabel")}
										name="lastXDays"
										pattern="^[0-9]*$"
										placeholder={ts("lastXDaysPlaceholder")}
										value={formValues.lastXDays ?? ""}
									/>
								</div>
							)}
							{lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.TIMEFRAME && (
								<div className="flex flex-col gap-2 w-full mb-4">
									<label className="flex flex-row items-center block mb-0 text-sm font-medium text-gray-900 appearance-none">
										Date Range
										<span className="ml-1">
											<Information
												information={"We will search for activities between the selected dates."}
											/>
										</span>
									</label>
									<div className="flex flex-row gap-4 w-full mb-3">
										<div className="w-fit min-w-[260px]">
											<Datepicker
												asSingle={true}
												containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
												inputClassName="h-[46px] relative transition-all duration-300 py-2.5 pl-4 pr-14 w-full border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-xl tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-blue-500/20"
												maxDate={formValues.dateTo ? new Date(formValues.dateTo) : new Date()}
												placeholder={ts("dateFromPlaceholder")}
												separator="-"
												showFooter={true}
												showShortcuts={true}
												useRange={false}
												value={
													formValues.dateFrom
														? {
																startDate: new Date(formValues.dateFrom),
																endDate: new Date(formValues.dateFrom),
															}
														: {
																startDate: null,
																endDate: null,
															}
												}
												onChange={(value: DateValueType) => {
													if (value) {
														setValue(
															"dateFrom",
															value?.startDate ? value.startDate.toISOString() : null,
															{
																shouldDirty: true,
																shouldValidate: true,
															}
														);

														setValue("lastXDays", null, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
											/>
										</div>
										<div className="w-fit min-w-[260px]">
											<Datepicker
												asSingle={true}
												containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
												inputClassName="h-[46px] relative transition-all duration-300 py-2.5 pl-4 pr-14 w-full border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-xl tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-blue-500/20"
												maxDate={new Date()}
												minDate={formValues.dateFrom ? new Date(formValues.dateFrom) : new Date()}
												placeholder={ts("dateToPlaceholder")}
												separator="-"
												showFooter={true}
												showShortcuts={true}
												useRange={false}
												value={
													formValues.dateTo
														? {
																startDate: new Date(formValues.dateTo),
																endDate: new Date(formValues.dateTo),
															}
														: {
																startDate: null,
																endDate: null,
															}
												}
												onChange={(value: DateValueType) => {
													if (value) {
														setValue(
															"dateTo",
															value?.endDate ? value.endDate.toISOString() : null,
															{
																shouldDirty: true,
																shouldValidate: true,
															}
														);

														setValue("lastXDays", null, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
											/>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				) : (
					""
				)}
				{formValues.type === FundingMaType.MA ? (
					<div>
						<div className="flex flex-row gap-4 w-full mb-4">
							<MultiAutoComplete
								data={FundingCountries as unknown as MultiAutoCompleteItem[]}
								selectedItems={
									formValues.countries?.map(
										(r) => FundingCountries.find((c) => c.id === r) ?? { id: r, title: r }
									) || []
								}
								handleSelect={function (value?: MultiAutoCompleteItem[]): void {
									if (value?.length && value?.length > 0) {
										setValue("countries", value?.map((r) => r.id.toString()) || [], {
											shouldDirty: true,
											shouldValidate: true,
										});
									} else {
										setValue("countries", null, {
											shouldDirty: true,
											shouldValidate: true,
										});
									}
								}}
								information={ts("countriesInformation")}
								label={ts("countriesLabel")}
								placeholder={ts("countriesPlaceholder")}
							/>
						</div>
						<div className="flex flex-row gap-4 w-full mb-4">
							<MultiAutoComplete
								handleSelect={function (value?: MultiAutoCompleteItem[]): void {
									if (value?.length && value?.length > 0) {
										setValue("category", value?.map((r) => r.id.toString()) || [], {
											shouldDirty: true,
											shouldValidate: true,
										});
									} else {
										setValue("category", null, {
											shouldDirty: true,
											shouldValidate: true,
										});
									}
								}}
								information={ts("categoryInformation")}
								label={ts("categoryLabel")}
								placeholder={ts("categoryPlaceholder")}
								data={FundingCategories as unknown as MultiAutoCompleteItem[]}
								selectedItems={
									formValues.category?.map(
										(r) => FundingCategories.find((c) => c.id === r) ?? { id: r, title: r }
									) || []
								}
							/>
						</div>
						<div className="flex flex-col gap-4 w-full">
							<div className="flex flex-row gap-4 w-full">
								<Radio
									isChecked={lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.LAST_X_DAYS}
									onChange={() => setLastIxDaysOrTimeframe(LastIxDaysOrTimeframe.LAST_X_DAYS)}
								>
									Last X Days
								</Radio>
								<Radio
									isChecked={lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.TIMEFRAME}
									onChange={() => setLastIxDaysOrTimeframe(LastIxDaysOrTimeframe.TIMEFRAME)}
								>
									Range
								</Radio>
							</div>
							{lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.LAST_X_DAYS && (
								<div className="flex flex-row gap-4 w-full mb-4">
									<InputField
										error={!!errors.lastXDays?.message}
										handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
											if (!isNaN(+event.target.value) && +event.target.value > 0) {
												setValue("lastXDays", Number(event.target.value), {
													shouldDirty: true,
													shouldValidate: true,
												});
												setValue("dateFrom", null, {
													shouldDirty: true,
													shouldValidate: true,
												});
												setValue("dateTo", null, {
													shouldDirty: true,
													shouldValidate: true,
												});
											} else if (event.target.value === "") {
												setValue("lastXDays", null, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}
										}}
										label={ts("lastXDaysLabel")}
										name="lastXDays"
										pattern="^[0-9]*$"
										placeholder={ts("lastXDaysPlaceholder")}
										value={formValues?.lastXDays ?? ""}
									/>
								</div>
							)}
							{lastIxDaysOrTimeframe === LastIxDaysOrTimeframe.TIMEFRAME && (
								<div className="flex flex-col gap-2 w-full mb-4">
									<label className="flex flex-row items-center block mb-0 text-sm font-medium text-gray-900 appearance-none">
										Date Range
										<span className="ml-1">
											<Information
												information={"We will search for activities between the selected dates."}
											/>
										</span>
									</label>
									<div className="flex flex-row gap-4 w-full mb-3">
										<div className="w-fit min-w-[260px]">
											<Datepicker
												asSingle={true}
												containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
												inputClassName="h-[46px] relative transition-all duration-300 py-2.5 pl-4 pr-14 w-full border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-xl tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-blue-500/20"
												maxDate={formValues.dateTo ? new Date(formValues.dateTo) : new Date()}
												placeholder={ts("dateFromPlaceholder")}
												separator="-"
												showFooter={true}
												showShortcuts={true}
												useRange={false}
												value={
													formValues?.dateFrom
														? {
																startDate: new Date(formValues.dateFrom),
																endDate: new Date(formValues.dateFrom),
															}
														: {
																startDate: null,
																endDate: null,
															}
												}
												onChange={(value: DateValueType) => {
													if (value) {
														setValue(
															"dateFrom",
															value?.startDate ? value.startDate.toISOString() : null,
															{
																shouldDirty: true,
																shouldValidate: true,
															}
														);
													}
												}}
											/>
										</div>
										<div className="w-fit min-w-[260px]">
											<Datepicker
												asSingle={true}
												containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
												inputClassName="h-[46px] relative transition-all duration-300 py-2.5 pl-4 pr-14 w-full border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-xl tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-blue-500/20"
												maxDate={new Date()}
												minDate={formValues.dateFrom ? new Date(formValues.dateFrom) : new Date()}
												placeholder={ts("dateToPlaceholder")}
												separator="-"
												showFooter={true}
												showShortcuts={true}
												useRange={false}
												value={
													formValues?.dateTo
														? {
																startDate: new Date(formValues.dateTo),
																endDate: new Date(formValues.dateTo),
															}
														: {
																startDate: null,
																endDate: null,
															}
												}
												onChange={(value: DateValueType) => {
													if (value) {
														setValue(
															"dateTo",
															value?.endDate ? value.endDate.toISOString() : null,
															{
																shouldDirty: true,
																shouldValidate: true,
															}
														);
													}
												}}
											/>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				) : (
					""
				)}

				{getAllErrors(error, formErrors).length ? (
					<div className="mb-4">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
				) : null}

				<div className="flex gap-4 w-full justify-end">
					{handleSelect && (
						<div className="flex max-w-[100px] w-full">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								title="cancel"
								onClick={() => {
									handleSelect(null);
									handleDuplicate?.(null);
									reset(initialState);
								}}
							/>
						</div>
					)}
					<div className="flex w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={
								!formValues.round &&
								!formValues.countries &&
								!formValues.category &&
								(!formValues.lastXDays || (!formValues.dateFrom && !formValues.dateTo))
							}
							isLoading={previewIsLoading}
							title="Preview"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();

								handlePreviewJob();
							}}
						/>
					</div>
					<div className="flex max-w-[150px] w-full">
						<Button
							isLoading={isLoading || updateIsLoading}
							title={buttonTitle}
							onClick={handleSubmit(onSubmit)}
						/>
					</div>
				</div>
			</div>
			{showPreviewModal && (
				<Modal
					handleClose={() => setShowPreviewModal(false)}
					isOpened={showPreviewModal}
					size="lg"
					title="Preview"
				>
					<Table
						columns={formValues.type === FundingMaType.FUNDING ? previewColumns : previewMaColumns}
						data={previewFundingMaData?.data || []}
						hideFooter={true}
						hidePagination={true}
					/>
				</Modal>
			)}
		</>
	);
};
